import React, { useRef, useState } from 'react';
import { ChevronDown, Paperclip } from 'lucide-react';
import type { PickedReference } from '../../types';
import type { ReferenceChoice, ReferenceChoiceKind } from '../../utils/workspaceEdit';
import { livePicks, pickedChoiceIds, togglePicked } from '../../utils/pickedReferences';
import { ReferencePicker } from './ReferencePicker';
import { chipButtonClass } from './composer/Composer';

/** The open workspace's project, whose other workspaces' Reference nodes are offered too. */
export interface ReferenceSharing {
  projectName: string;
  /** Still reading the project's other workspaces. */
  loading: boolean;
  /** Workspaces read so far; picks from the others are kept as they are. */
  readWorkspaceIds: ReadonlySet<string>;
  /** Reads them again, when the list opens. */
  refresh: () => void;
}

interface AddReferencesButtonProps {
  /** What this node can use: pictures, and for some video models clips and audio too. */
  kinds: ReferenceChoiceKind[];
  picked?: PickedReference[];
  /** Media already wired into the node; its Reference node shows as connected. */
  connectedUrls: string[];
  /** The workspace's Reference nodes, read when the list opens. */
  getChoices?: () => ReferenceChoice[];
  onChange?: (next: PickedReference[]) => void;
  userId?: string;
  ownerUserId?: string;
  workspaceName?: string;
  disabled?: boolean;
  /** In a project: its other workspaces' Reference nodes are in the list as well. */
  sharing?: ReferenceSharing;
}

type AddReferencesOptions = Omit<AddReferencesButtonProps, 'disabled'>;

/**
 * The list of Reference nodes, opened from any element: the Add reference pill, or an input
 * tile that asks for one kind only (`open(anchor, ['video'])`). Each click in the list adds or
 * removes one; it stays open until Done.
 */
export const useAddReferences = ({
  kinds, picked = [], connectedUrls, getChoices, onChange, userId, ownerUserId, workspaceName, sharing,
}: AddReferencesOptions) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [onlyKinds, setOnlyKinds] = useState<ReferenceChoiceKind[] | null>(null);
  // Read at every render while open, so references from other workspaces appear as they are read
  const choices: ReferenceChoice[] = anchor && getChoices ? getChoices() : [];
  // The latest list, so quick clicks in a row each build on the one before
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  const available = !!getChoices && !!onChange && kinds.length > 0;

  const open = (element: HTMLElement, only?: ReferenceChoiceKind[]) => {
    if (!available) return;
    if (anchor === element) { setAnchor(null); return; }
    sharing?.refresh();
    setOnlyKinds(only && only.length > 0 ? only : null);
    setAnchor(element);
  };
  // Picks whose Reference node was deleted are left out, and dropped with the next change
  const alive = (list: PickedReference[]) => livePicks(list, choices, sharing?.readWorkspaceIds ?? new Set());
  const pick = (choice: ReferenceChoice) => {
    if (!onChange) return;
    const next = togglePicked(alive(pickedRef.current), choice);
    pickedRef.current = next;
    onChange(next);
  };
  const picker = anchor ? (
    <ReferencePicker
      anchor={anchor}
      choices={choices}
      kinds={onlyKinds ?? kinds}
      groupBy="tag"
      multiple
      selectedIds={pickedChoiceIds(alive(picked))}
      lockedIds={choices.filter(choice => connectedUrls.includes(choice.url)).map(choice => choice.id)}
      userId={userId}
      ownerUserId={ownerUserId}
      workspaceName={workspaceName}
      sharedFrom={sharing ? { projectName: sharing.projectName, loading: sharing.loading } : undefined}
      onPick={pick}
      onClose={() => setAnchor(null)}
    />
  ) : null;
  return { available, open, anchor, picker };
};

/**
 * Opens a list of the workspace's Reference nodes, by tag, to use in this node without
 * wiring them. Each click adds or removes one; the list stays open until Done. It is a chip
 * that opens a list, so it looks like the composer's setting chips.
 */
export const AddReferencesButton: React.FC<AddReferencesButtonProps> = (props) => {
  const { disabled = false, sharing } = props;
  const { available, open, anchor, picker } = useAddReferences(props);
  if (!available) return null;
  const toggle = (e: React.MouseEvent<HTMLElement>) => open(e.currentTarget);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        title={sharing ? `Use a Reference node from ${sharing.projectName} without connecting it` : 'Use a Reference node from this workspace without connecting it'}
        // (`aria-expanded` above is what shows the chip as open)
        className={chipButtonClass()}
        data-add-references
      >
        <Paperclip size={14} className="shrink-0" aria-hidden="true" />
        Add reference
        <ChevronDown size={12} className={`shrink-0 opacity-60 transition-transform ${anchor ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {picker}
    </>
  );
};

export default AddReferencesButton;
